<script setup>
// Shown on arrival when a saved schedule has become self-conflicting: session
// times shifted in the catalog and things someone already holds now overlap
// (Nick, 2026-08-20; Figma 4753:107137).
//
// Distinct from double-book.vue, which refuses an ADD. Nothing is being added
// here — the clash appeared underneath them — so this dialog only explains and
// hands them to the place they can fix it.
//
// The comp singled out the session that MOVED, under its own "New Date/Time"
// heading. We cannot tell which one that is: last_modified_time on the sessions
// table is the sync's own run timestamp, identical across every row, so nothing
// distinguishes a session whose time changed from one that was always there.
// Rather than guess, this lists what conflicts NOW (Jack, 2026-08-20).
//
// Takes GROUPS, not a flat list — the shape findScheduleConflicts returns. Two
// separate clashes flattened into one list would read as though all four
// sessions fight each other, which is a different and wrong claim.
const props = defineProps({
    // [[ { unique_id, title, start_date_and_time, end_date_and_time }, ... ], ...]
    // Catalog sessions AND custom events, which is why the heading names both.
    groups: { type: Array, default: () => [] },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

import { modal_button_outline_2026 } from '~~/shared/utilities/ui';

const result = ref(null);

const conflictCount = computed(() =>
    props.groups.reduce((n, g) => n + (Array.isArray(g) ? g.length : 0), 0)
);

// WED • SEPT 16 and 10:30am - 11:30am ET, per the comp. Conference-local, not
// the viewer's zone — everything else on the agenda reads in event time.
const dayLabel = (item) => {
    const d = new Date(item?.start_date_and_time);
    if (Number.isNaN(d.getTime())) return '';
    const fmt = (o) => d.toLocaleString('en-US', { timeZone: 'America/New_York', ...o });
    return `${fmt({ weekday: 'short' })} • ${fmt({ month: 'short' })} ${fmt({ day: 'numeric' })}`.toUpperCase();
};
const timeLabel = (item) => {
    const s = new Date(item?.start_date_and_time);
    const e = new Date(item?.end_date_and_time);
    if (Number.isNaN(s.getTime())) return '';
    const t = (d) => d.toLocaleString('en-US', {
        timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
    }).replace(' ', '').toLowerCase();
    return Number.isNaN(e.getTime()) ? `${t(s)} ET` : `${t(s)} - ${t(e)} ET`;
};

const fix = () => {
    result.value = 'fix';
    open.value = false;
};

// Resolves through emit('close', value) like the other dialogs: the opener acts
// on the answer, so this stays presentational. Anything but "Take Me to Fix
// This" resolves null.
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        headline="We’re sorry, but you’ll need to take action on sessions that were added to My Schedule."
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[30px] w-full">
            <!-- White notice: what happened and what they have to do about it. -->
            <div class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                    We had to adjust the date/time of some sessions.
                    {{ conflictCount }} sessions on My Schedule are now within the same time slot.
                    You’ll need to decide which sessions to remove so that there are no overlapping
                    sessions on My Schedule.
                </p>
            </div>

            <!-- One block per clash. Kept separate rather than concatenated: two
                 unrelated clashes in a single list would read as one four-way
                 pile-up. -->
            <div
                v-for="(group, i) in groups"
                :key="`conflict-${i}`"
                class="flex flex-col items-start gap-[14px] w-full"
            >
                <span class="inline-flex items-center rounded-[25px] bg-white px-[15px] py-[5px]
                    font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-(--magenta)">
                    Sessions/My Events in the Same Time Slot
                </span>
                <ul class="flex flex-col w-full list-none m-0 p-0 border-t border-(--dark-pink)">
                    <!-- These rows are the SAME rows as the Save a Seat return-visit
                         prompt's (Nick, Asana 1217708959827694): the two modals list a schedule the
                         same way, so the day, time and session name must read
                         identically. The classes below are save-seats-prompt.vue's
                         verbatim — day 13px/1.04px in a 106px column, time 148px in
                         --darker-pink at normal weight, and the title in the SERIF
                         face, which is the difference that showed most.
                         Mobile is unchanged and was already shared: stacked, with
                         day and time on one line above the title (Figma 5999:151065
                         — 14px day/date sized to its text, 16px to the time, 15px of
                         row padding, 3px down to the title). -->
                    <li
                        v-for="item in group"
                        :key="item.unique_id"
                        class="flex flex-col gap-[3px] w-full py-[15px] border-b border-(--dark-pink) text-left
                            sm:flex-row sm:items-center sm:gap-[30px] sm:py-[12px]"
                    >
                        <div class="flex items-center gap-[16px] sm:gap-[30px] shrink-0">
                            <span class="w-auto sm:w-[106px] whitespace-nowrap font-hubspot-sans font-medium text-[14px] sm:text-[13px] leading-none tracking-[1.12px] sm:tracking-[1.04px] uppercase text-(--magenta)">{{ dayLabel(item) }}</span>
                            <span class="w-[148px] font-hubspot-sans font-normal text-[14px] leading-[1.5] tracking-[0.21px] text-(--darker-pink)">{{ timeLabel(item) }}</span>
                        </div>
                        <span class="flex-1 min-w-0 font-hubspot-serif font-normal text-[15px] leading-[1.4] tracking-[0.15px] text-(--magenta)">{{ item.title }}</span>
                    </li>
                </ul>
            </div>

            <button
                type="button"
                data-test="schedule-conflict-fix"
                :class="modal_button_outline_2026"
                @click="fix"
            >Take Me to Fix This</button>
        </div>

        <template #actions>
            <button
                type="button"
                data-test="schedule-conflict-close"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Close</button>
        </template>
    </FragmentsModalShell>
</template>
